import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl } from "../lib/api";
import { Trash2, CreditCard, Tag } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, total } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [checking, setChecking] = useState(false);

  const applyCoupon = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.get("/coupons/validate", { params: { code: code.trim(), total } });
      setCoupon(data);
      toast.success(`-$${data.discount.toFixed(2)} applied`);
    } catch (err) {
      setCoupon(null);
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setChecking(false); }
  };

  const removeCoupon = () => { setCoupon(null); setCode(""); };

  const grandTotal = Math.max((total - (coupon?.discount || 0)), 0);

  const checkout = async () => {
    if (!user) { toast.message("Log in to check out"); nav("/login?redirect=/cart"); return; }
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        item_ids: items.map((i) => i.id),
        origin_url: window.location.origin,
        coupon_code: coupon?.code || null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-10" data-testid="cart-page">
      <span className="tag-sticker bg-yellow text-xl inline-block mb-2 rotate-tilt-l">your loot</span>
      <h1 className="font-display text-5xl uppercase">The cart</h1>

      {items.length === 0 ? (
        <div className="brut p-10 text-center mt-8" data-testid="cart-empty">
          <p className="font-display text-3xl uppercase">Empty as a Sunday closet.</p>
          <Link to="/shop" className="brut-btn bg-orange text-white px-6 py-3 mt-6 inline-block">Go shop</Link>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8 flex flex-col gap-4">
            {items.map((it) => (
              <div key={it.id} className="brut p-3 flex items-center gap-4" data-testid={`cart-row-${it.id}`}>
                <div className="w-20 h-24 brut-sm overflow-hidden flex-shrink-0">
                  {it.image ? <img src={fileUrl(it.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#f3efe6]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/item/${it.id}`} className="font-display text-xl uppercase block truncate">{it.title}</Link>
                  <div className="font-bold mt-1">${Number(it.price).toFixed(2)}</div>
                </div>
                <button onClick={() => remove(it.id)} className="brut-btn bg-white px-3 py-2" data-testid={`remove-${it.id}`} title="Remove">
                  <Trash2 size={18} strokeWidth={3} />
                </button>
              </div>
            ))}
          </div>
          <div className="md:col-span-4">
            <div className="brut p-5 sticky top-24">
              <h2 className="font-display text-2xl uppercase">Summary</h2>
              <div className="flex justify-between mt-4 text-lg"><span>Subtotal</span><span>${total.toFixed(2)}</span></div>
              {coupon && (
                <div className="flex justify-between mt-2 text-lg text-pink" data-testid="discount-row">
                  <span>{coupon.code}</span><span>-${coupon.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between mt-2 font-display text-2xl border-t-4 border-[#1A1A1A] pt-2">
                <span>Total</span><span>${grandTotal.toFixed(2)}</span>
              </div>

              {!coupon ? (
                <form onSubmit={applyCoupon} className="mt-4 flex gap-2">
                  <div className="brut-input flex items-center gap-2 py-2 flex-1">
                    <Tag size={16} strokeWidth={3} />
                    <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="coupon" className="bg-transparent outline-none w-full uppercase" data-testid="coupon-input" />
                  </div>
                  <button disabled={checking} className="brut-btn bg-cyan px-3 py-2 text-sm" data-testid="apply-coupon-btn">{checking ? "…" : "apply"}</button>
                </form>
              ) : (
                <button onClick={removeCoupon} className="text-sm font-bold underline mt-3" data-testid="remove-coupon-btn">remove coupon</button>
              )}

              <button onClick={checkout} disabled={loading} className="brut-btn bg-orange text-white w-full py-4 mt-6 flex items-center justify-center gap-2" data-testid="checkout-btn">
                <CreditCard size={20} strokeWidth={3} />
                {loading ? "Redirecting…" : "Checkout"}
              </button>
              <p className="text-xs mt-3 opacity-70">Secured by Stripe. Cards, Apple Pay, Cash App Pay.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
